import { useState, useCallback } from 'react';
import Alert, { AlertOptions, AlertResult, AlertType, AlertPosition } from './Alert';

interface UseAlertReturn {
  showAlert: (options: AlertOptions) => Promise<AlertResult>;
  AlertComponent: React.FC;
}

interface InternalAlertOptions extends AlertOptions {
  onClose?: (result: AlertResult) => void;
}

export const useAlert = (): UseAlertReturn => {
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    options: InternalAlertOptions;
  }>({
    isOpen: false,
    options: {},
  });

  const showAlert = useCallback((options: AlertOptions): Promise<AlertResult> => {
    return new Promise((resolve) => {
      setAlertState({
        isOpen: true,
        options: {
          ...options,
          onClose: (result: AlertResult) => {
            setAlertState({ isOpen: false, options: {} });
            resolve(result);
          },
        },
      });
    });
  }, []);

  const AlertComponent: React.FC = () => (
    <Alert
      isOpen={alertState.isOpen}
      onClose={alertState.options.onClose || (() => {})}
      {...alertState.options}
    />
  );

  return { showAlert, AlertComponent };
};

// Convenience functions for common alert types
export const createAlertHelpers = (showAlert: (options: AlertOptions) => Promise<AlertResult>) => ({
  // Success alert
  success: (title: string, text?: string, options?: Partial<AlertOptions>) =>
    showAlert({
      title,
      text,
      icon: 'success',
      confirmButtonText: 'OK',
      ...options,
    }),

  // Error alert
  error: (title: string, text?: string, options?: Partial<AlertOptions>) =>
    showAlert({
      title,
      text,
      icon: 'error',
      confirmButtonText: 'OK',
      ...options,
    }),

  // Warning alert
  warning: (title: string, text?: string, options?: Partial<AlertOptions>) =>
    showAlert({
      title,
      text,
      icon: 'warning',
      confirmButtonText: 'OK',
      ...options,
    }),

  // Info alert
  info: (title: string, text?: string, options?: Partial<AlertOptions>) =>
    showAlert({
      title,
      text,
      icon: 'info',
      confirmButtonText: 'OK',
      ...options,
    }),

  // Confirmation dialog
  confirm: (title: string, text?: string, options?: Partial<AlertOptions>) =>
    showAlert({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      ...options,
    }),

  // Delete confirmation
  deleteConfirm: (title: string = 'Are you sure?', text: string = "You won't be able to revert this!", options?: Partial<AlertOptions>) =>
    showAlert({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6b7280',
      ...options,
    }),

  // Logout confirmation
  logoutConfirm: (title: string = 'Confirm Logout', text: string = 'Are you sure you want to log out?', options?: Partial<AlertOptions>) =>
    showAlert({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, log out',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      ...options,
    }),
});

export default useAlert;
